/** @jsxImportSource @emotion/react */
import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { closeButtonCSS, contentCSS, portalCSS } from '@birdi/modal-kit';
import CloseSVG from '@birdi/icons/close.svg';
import UploadSVG from '@birdi/icons/upload.svg';
import MapSVG from '@birdi/icons/map.svg';
import { InformationIndicator } from '@birdi/components-common';

const UPLOAD_TO_NEW_MAP_IMAGE =
  'https://birdi-public.s3.ap-southeast-2.amazonaws.com/upload-to-new-map.png';
const UPLOAD_TO_EXISTING_MAP_IMAGE =
  'https://birdi-public.s3.ap-southeast-2.amazonaws.com/upload-to-existing-map.png';

export type UploadToMapModalVariant = 'upload' | 'process';

const VARIANT_COPY: Record<
  UploadToMapModalVariant,
  {
    title: string;
    description: string;
    infoTooltip: string;
    createCardTitle: string;
    createCardDescription: string;
    existingCardTitle: string;
    existingCardDescription: string;
  }
> = {
  upload: {
    title: 'Upload to a map',
    description: 'Where would you like to upload?',
    infoTooltip:
      'Create a new map to upload data, or add files to a map you already have.',
    createCardTitle: 'Create a new map',
    createCardDescription: 'Start a fresh map with new data',
    existingCardTitle: 'Add to existing map',
    existingCardDescription: 'Add more data to an existing map',
  },
  process: {
    title: 'Process map data',
    description: 'What would you like to process?',
    infoTooltip:
      'Upload a new dataset to process, or process files from an existing map.',
    createCardTitle: 'Upload a new data set',
    createCardDescription: 'Upload and process new imagery',
    existingCardTitle: 'Existing map',
    existingCardDescription: 'Process images from a map folder',
  },
};

export interface UploadToMapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Upload flow vs process-from-dashboard flow (copy + primary icon differ). */
  variant: UploadToMapModalVariant;
  /** When true, the “existing map” card is disabled (e.g. process flow — not ready yet). */
  disableAddToExisting?: boolean;
  onCreateNewMap?: () => void;
  onAddToExistingMap?: () => void;
}

export const UploadToMapModal: React.FC<UploadToMapModalProps> = ({
  open,
  onOpenChange,
  variant,
  disableAddToExisting = false,
  onCreateNewMap,
  onAddToExistingMap,
}) => {
  const theme = useTheme() as BirdiTheme;
  const copy = VARIANT_COPY[variant];
  const titleIconCss = css`
    width: 1.5rem;
    height: 1.5rem;
    flex-shrink: 0;
  `;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <div css={portalCSS}>
          <Dialog.Content
            data-testid="upload-to-map-modal"
            css={(t) => css`
              ${contentCSS(t)};
              width: 52em;
              max-width: 90vw;
              padding: 2em;
              padding-top: 2.25em;
            `}
          >
            <Dialog.Close
              css={closeButtonCSS}
              data-testid="upload-to-map-modal-close"
              aria-label="Close"
            >
              <CloseSVG />
            </Dialog.Close>

            <Dialog.Title asChild>
              <h2
                css={css`
                  margin: 0 2.5rem 0 0;
                  font-size: 1.25rem;
                  font-weight: 700;
                  color: ${theme.midnight};
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                  flex-wrap: wrap;
                `}
              >
                {variant === 'process' ? (
                  <MapSVG css={titleIconCss} aria-hidden="true" />
                ) : (
                  <UploadSVG css={titleIconCss} aria-hidden="true" />
                )}
                <span>{copy.title}</span>
                <InformationIndicator message={copy.infoTooltip} />
              </h2>
            </Dialog.Title>

            <div
              css={css`
                height: 1px;
                background: ${theme.mono30};
                margin: 1rem 0 1.25rem;
              `}
            />

            <Dialog.Description asChild>
              <p
                css={css`
                  margin: 0 0 1.25rem;
                  color: ${theme.mono50};
                  font-size: 0.9375rem;
                `}
              >
                {copy.description}
              </p>
            </Dialog.Description>

            <div
              css={css`
                display: flex;
                gap: 1rem;
                align-items: stretch;
              `}
            >
              <button
                type="button"
                data-testid="upload-to-map-modal-create-new"
                css={choiceCardCss(theme)}
                onClick={() => onCreateNewMap?.()}
              >
                <img
                  src={UPLOAD_TO_NEW_MAP_IMAGE}
                  alt=""
                  css={cardImageCss}
                />
                <span css={cardTitleCss(theme)}>{copy.createCardTitle}</span>
                <span css={cardDescCss(theme)}>{copy.createCardDescription}</span>
              </button>

              <button
                type="button"
                data-testid="upload-to-map-modal-add-existing"
                disabled={disableAddToExisting}
                css={choiceCardCss(theme, disableAddToExisting)}
                onClick={() => {
                  if (disableAddToExisting) return;
                  onAddToExistingMap?.();
                }}
              >
                <img
                  src={UPLOAD_TO_EXISTING_MAP_IMAGE}
                  alt=""
                  css={cardImageCss}
                />
                <span css={cardTitleCss(theme)}>{copy.existingCardTitle}</span>
                <span css={cardDescCss(theme)}>
                  {copy.existingCardDescription}
                </span>
              </button>
            </div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const cardImageCss = css`
  width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
  margin-bottom: 0.75rem;
`;

const choiceCardCss = (theme: BirdiTheme, disabled?: boolean) => css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem;
  border: 1px solid ${disabled ? theme.mono40 : theme.mono30};
  border-radius: 12px;
  background: ${disabled ? theme.mono20 : theme.mono0};
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  box-sizing: border-box;
  transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
  opacity: ${disabled ? 0.55 : 1};
  &:hover {
    background: ${disabled ? theme.mono20 : theme.mono10};
    border-color: ${disabled ? theme.mono40 : theme.mono40};
    box-shadow: ${disabled ? 'none' : `0 0 10px ${theme.mono30}`};
  }
  &:focus-visible {
    outline: 2px solid ${theme.deepOcean};
    outline-offset: 2px;
  }
`;

const cardTitleCss = (theme: BirdiTheme) => css`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${theme.midnight};
  margin: 0.5em 0;
`;

const cardDescCss = (theme: BirdiTheme) => css`
  font-size: 1rem;
  line-height: 1.5;
  color: ${theme.mono50};
  margin: 0.5em 0;
`;
